import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileUpload } from '@/components/FileUpload';
import { caseStudiesApi } from '@/lib/api/case-studies';
import type { CaseStudyAttachmentDto } from '@paladin-monorepo/common';

const ACCEPTED_TYPES = [
  'application/pdf',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel.sheet.macroEnabled.12',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/acad',
  'image/png',
  'image/jpeg',
];

const PENDING_PREFIX = 'pending-';

interface CaseStudyAttachmentsProps {
  caseStudyId?: string;
  attachments: CaseStudyAttachmentDto[];
  pendingFiles?: File[];
  onAttachmentsChange: (attachments: CaseStudyAttachmentDto[]) => void;
  onPendingFilesChange?: (files: File[]) => void;
}

export function CaseStudyAttachments({
  caseStudyId,
  attachments,
  pendingFiles = [],
  onAttachmentsChange,
  onPendingFilesChange,
}: CaseStudyAttachmentsProps) {
  const { t } = useTranslation();
  const [isUploading, setIsUploading] = useState(false);
  const canUpload = !!caseStudyId;

  const filesForUpload = [
    ...attachments.map((a) => ({
      id: a.id,
      originalName: a.originalName,
      size: a.size,
      mimeType: a.mimeType,
      description: a.description,
      _isQueued: false as const,
    })),
    ...pendingFiles.map((file, index) => ({
      id: `${PENDING_PREFIX}${index}`,
      originalName: file.name,
      size: file.size,
      mimeType: file.type,
      description: undefined,
      _isQueued: true as const,
    })),
  ];

  const onFileUpload = useCallback(
    async (newFiles: File[]) => {
      if (!canUpload) {
        onPendingFilesChange?.([...pendingFiles, ...newFiles]);
        return;
      }

      setIsUploading(true);
      try {
        const uploaded: CaseStudyAttachmentDto[] = [];
        for (const file of newFiles) {
          const attachment = await caseStudiesApi.uploadAttachment(caseStudyId, file);
          uploaded.push(attachment);
        }
        onAttachmentsChange([...attachments, ...uploaded]);
        toast.success(t('profile.caseStudies.attachments.uploadSuccess'));
      } catch {
        toast.error(t('profile.caseStudies.attachments.uploadError'));
      } finally {
        setIsUploading(false);
      }
    },
    [attachments, canUpload, caseStudyId, onAttachmentsChange, onPendingFilesChange, pendingFiles, t],
  );

  const onFileRemove = useCallback(
    async (fileId: string) => {
      if (fileId.startsWith(PENDING_PREFIX)) {
        const index = parseInt(fileId.replace(PENDING_PREFIX, ''), 10);
        onPendingFilesChange?.(pendingFiles.filter((_, i) => i !== index));
        return;
      }

      if (!caseStudyId) return;

      try {
        await caseStudiesApi.deleteAttachment(caseStudyId, fileId);
        onAttachmentsChange(attachments.filter((a) => a.id !== fileId));
        toast.success(t('profile.caseStudies.attachments.deleteSuccess'));
      } catch {
        toast.error(t('profile.caseStudies.attachments.deleteError'));
      }
    },
    [attachments, caseStudyId, onAttachmentsChange, onPendingFilesChange, pendingFiles, t],
  );

  const onFileReplace = useCallback(
    async (fileId: string, nextFile: File) => {
      if (fileId.startsWith(PENDING_PREFIX)) {
        const index = parseInt(fileId.replace(PENDING_PREFIX, ''), 10);
        onPendingFilesChange?.(
          pendingFiles.map((file, i) => (i === index ? nextFile : file)),
        );
        return;
      }
      if (!caseStudyId) return;
      try {
        const updated = await caseStudiesApi.replaceAttachment(caseStudyId, fileId, nextFile);
        onAttachmentsChange(attachments.map((a) => (a.id === fileId ? updated : a)));
        toast.success(t('profile.caseStudies.attachments.replaceSuccess'));
      } catch {
        toast.error(t('profile.caseStudies.attachments.replaceError'));
      }
    },
    [attachments, caseStudyId, onAttachmentsChange, onPendingFilesChange, pendingFiles, t],
  );

  return (
    <div className="space-y-2">
      <p className="text-sm font-medium">{t('profile.caseStudies.attachments.title')}</p>
      <p className="text-sm text-muted-foreground">
        {canUpload
          ? t('profile.caseStudies.attachments.description')
          : t('profile.caseStudies.attachments.saveFirstHint')}
      </p>
      <FileUpload
        onFileUpload={onFileUpload}
        onFileRemove={onFileRemove}
        onFileUpdate={() => {}}
        onFileReplace={onFileReplace}
        files={filesForUpload}
        acceptedTypes={ACCEPTED_TYPES}
        className={isUploading ? 'pointer-events-none opacity-60' : undefined}
      />
    </div>
  );
}

export async function uploadPendingCaseStudyFiles(
  caseStudyId: string,
  pendingFiles: File[],
): Promise<CaseStudyAttachmentDto[]> {
  const uploaded: CaseStudyAttachmentDto[] = [];
  for (const file of pendingFiles) {
    uploaded.push(await caseStudiesApi.uploadAttachment(caseStudyId, file));
  }
  return uploaded;
}
